import React, { Fragment, useEffect, useState, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { push } from "connected-react-router";
import * as numberUtils from "util/number";
import { bool, shape, arrayOf, number } from "prop-types";
import { actions as loansActions, selectors as loansSelectors } from "reducers/loans";
import * as Yup from "yup";
import * as i18n from "util/i18n";
import useSchema from "hooks/useSchema";
import MainContainer from "pages/_components/MainContainer";
import CustomButton from "pages/_components/CustomButton/CustomButton";
import { Col, Row } from "react-bootstrap";
import { selectors as environmentSelectors, actions as environmentActions } from "reducers/environment";
import { Field, Form, Formik } from "formik";
import AmountField from "pages/_components/fields/formik/AmountField";
import Selector from "pages/_components/fields/formik/Selector";
import CustomProductHead from "pages/_components/CustomProductHead";
import ViewTitle from "pages/_components/header/components/ViewTitle";
import GridTable from "pages/_components/GridTable/GridTable";
import Uploader from "pages/environment/_components/Uploader";
import I18n from "pages/_components/I18n";

import * as configUtils from "util/config";

const CustomLoanForm = ({ fetching, destinyFundList, minAmount, documentationBackup, destinyFund, amount }) => {
    const formRef = useRef(null);
    const dispatch = useDispatch();
    const { stringNullableRequired, numberNullableRequired } = useSchema();
    const [selectedDestinyFund, setSelectedDestinyFund] = useState(null);
    const [currentAmount, setCurrentAmount] = useState(null);

    useEffect(() => {
        dispatch(loansActions.unsecuresLoansApplicationPre("custom"));
    }, []);

    const thubanCodeLastBalance = configUtils.get("loans.thuban.code.lastBalance", "0A13");
    const thubanCodeSalesAfterBalance = configUtils.get("loans.thuban.code.salesAfterBalance", "75");
    const thubanCodeCashflow = configUtils.get("loans.thuban.code.cashflow ", "76");

    // Formik Validation Control
    const FORM_ID = "customLoanForm";

    const handleChangeDestinyFund = (value) => {
        setSelectedDestinyFund(value);
    };

    const validateAmount = (amount) => {
        setCurrentAmount(amount);
        return parseFloat(minAmount) <= amount;
    };

    const formikProps = {
        validateOnChange: true,
        validateOnBlur: false,
        initialValues: {
            destinyFund: destinyFund?.value || "",
            amountField: { amount: amount || "" },
            documentLastBalance: "",
            documentLastBalanceName: "",
            documentSalesAfterBalance: "",
            documentSalesAfterBalanceName: "",
            documentCashflow: "",
            documentCashflowName: "",
        },
        validationSchema: () =>
            Yup.object().shape({
                destinyFund: stringNullableRequired,
                amountField: Yup.object()
                    .shape({
                        amount: numberNullableRequired,
                    })
                    .test(
                        "amount",
                        i18n.get("transfer.amount.error"), // eslint-disable-next-line func-names
                        (value) => validateAmount(value.amount),
                    ),
            }),
        onSubmit: (values) => {
            if (values) {
                const { amountField, destinyFund } = values;
                const finalValues = {
                    documentLastBalance: values?.documentLastBalance,
                    documentLastBalanceName: values?.documentLastBalanceName,
                    documentSalesAfterBalance: values?.documentSalesAfterBalance,
                    documentSalesAfterBalanceName: values?.documentSalesAfterBalanceName,
                    documentCashflow: values?.documentCashflow,
                    documentCashflowName: values?.documentCashflowName,
                    amount: amountField.amount,
                    destinyFund,
                };
                dispatch(environmentActions.setDocumentationBackup(getDocumentData(finalValues)));

                dispatch(loansActions.customLoansApplicationConfirm(finalValues));
                dispatch(push("/requestCustomLoan/confirm"));
            }
        },
    };
    const currencies = [{ id: "ARS", label: i18n.get(`currency.label.ARS`) }];

    const getDataDocument = () => {
        const historicDocuments = {};

        if (!documentationBackup?.length) {
            return historicDocuments;
        }

        documentationBackup.forEach((item) => {
            const { documentKey } = item;
            historicDocuments[documentKey] = item;
        });
        return historicDocuments;
    };

    const getDocumentData = (values) => {
        const documentsUploaded = [
            {
                documentType: thubanCodeLastBalance,
                documentID: values.documentLastBalance,
                documentKey: "documentLastBalance",
                name: values.documentLastBalanceName,
            },
            {
                documentType: thubanCodeSalesAfterBalance,
                documentID: values.documentSalesAfterBalance,
                documentKey: "documentSalesAfterBalance",
                name: values.documentSalesAfterBalanceName,
            },
            {
                documentType: thubanCodeCashflow,
                documentID: values.documentCashflow,
                documentKey: "documentCashflow",
                name: values.documentCashflowName,
            }
        ];
        return documentsUploaded;
    };
    const fieldsAreValid = (values) =>{
        return (values.amountField?.amount != "" && values.destinyFund != "" &&
                            values.documentLastBalance != "" && values?.documentSalesAfterBalance != ""
                            && values?.documentCashflow != "")
    }
    return (
        <Fragment>
            <CustomProductHead titleId="loans.customLoan.request.header.title" backLinkTo="/applyForLoan">
                <div className="toolbar-item view-title">
                    <ViewTitle id="loans.customLoan.request.header.title" />
                </div>
            </CustomProductHead>
            <MainContainer showLoader={fetching} className="loans">
                <Formik {...formikProps} ref={formRef}>
                    {(formik) => (
                        <Form id={FORM_ID}>
                            <Row className="form-background">
                                <Col xs={12} md={5} mdOffset={1}>
                                    <Field
                                        component={AmountField}
                                        labelText={`${i18n.get("loans.customLoan.form.amount")}`}
                                        name="amountField"
                                        disable={!minAmount === null}
                                        data={{ options: currencies }}
                                        clearable={false}
                                        maxLength={16}
                                        disableSelect
                                        fixedDecimalScale
                                    />
                                    {minAmount !== null && (
                                        <p
                                            style={{
                                                textAlign: "right",
                                                marginTop: "-15px",
                                                color: "var(--color-blue)",
                                                fontSize: "var(--fontSize6)",
                                            }}>{`${i18n.get("", "Mínimo $")}${numberUtils.formatAmountStrDouble(
                                            minAmount,
                                            undefined,
                                            ".",
                                        )} `}</p>
                                    )}
                                </Col>
                                <Col xs={12} md={5} mdOffset={6}>
                                    <Field
                                        component={Selector}
                                        idForm={FORM_ID}
                                        name="destinyFund"
                                        label={`${i18n.get("loans.customLoan.form.destinyFunds")}`}
                                        placeholder={i18n.get("", "Seleccionar")}
                                        disabled={!destinyFundList?.length}
                                        options={destinyFundList}
                                        onChange={handleChangeDestinyFund}
                                    />
                                </Col>
                                <Col
                                    className="data-label"
                                    style={{ fontSize: "var(--fontSize2)", padding: "8px" }}
                                    xs={12}
                                    md={10}
                                    mdOffset={1}>
                                    <I18n id="loans.customLoan.form.documentation.title" />
                                </Col>
                                <Col className="data-label" xs={12} md={10} mdOffset={1} style={{ padding: "8px" }}>
                                    <div className="grid-table-data col-start-2 col-span-10 form-documentation">
                                        <I18n id="loans.customLoan.form.documentation.file.lastBalance" />
                                    </div>
                                    <div className="grid-table-data col-start-1 col-span-12">
                                        <Field
                                            component={Uploader}
                                            idForm={FORM_ID}
                                            name="documentLastBalance"
                                            label={i18n.get("loans.common.form.uploadFile.label")}
                                            extensionDocument="pdf"
                                            documentCode={thubanCodeLastBalance}
                                            defaultDocument={{
                                                documentId: getDataDocument()?.documentLastBalance,
                                                documentName: getDataDocument()?.documentLastBalance?.name,
                                            }}
                                            withDocumentName
                                            isOnboarding={false}
                                        />
                                    </div>
                                </Col>
                                <Col className="data-label " xs={12} md={10} mdOffset={1}>
                                    <div className="grid-table-data col-start-2 col-span-10 form-documentation">
                                        <I18n id="loans.customLoan.form.documentation.file.salesAfterBalance" />
                                    </div>
                                    <div className="grid-table-data col-start-1 col-span-12">
                                        <Field
                                            component={Uploader}
                                            idForm={FORM_ID}
                                            name="documentSalesAfterBalance"
                                            label={i18n.get("loans.common.form.uploadFile.label")}
                                            extensionDocument="pdf"
                                            documentCode={thubanCodeSalesAfterBalance}
                                            defaultDocument={{
                                                documentId: getDataDocument()?.documentSalesAfterBalance,
                                                documentName: getDataDocument()?.documentSalesAfterBalance?.name,
                                            }}
                                            withDocumentName
                                            isOnboarding={false}
                                        />
                                    </div>
                                </Col>
                                <Col className="data-label form-documentation" xs={12} md={10} mdOffset={1}>
                                    <div className="grid-table-data col-start-2 col-span-10 form-documentation">
                                        <I18n id="loans.customLoan.form.documentation.file.cashflow" />
                                    </div>
                                    <div className="grid-table-data col-start-1 col-span-12">
                                        <Field
                                            component={Uploader}
                                            idForm={FORM_ID}
                                            name="documentCashflow"
                                            label={i18n.get("loans.common.form.uploadFile.label")}
                                            extensionDocument="pdf"
                                            documentCode={thubanCodeCashflow}
                                            defaultDocument={{
                                                documentId: getDataDocument()?.documentCashflow,
                                                documentName: getDataDocument()?.documentCashflow?.name,
                                            }}
                                            withDocumentName
                                            isOnboarding={false}
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <GridTable className="form-actions grid-table-buttons mb-5">
                                <GridTable.Container columns={12} columnsTemplate="fixed">
                                    <GridTable.Data columnStart={5} columnWidth={4}>
                                        <CustomButton
                                            text={i18n.get("loans.customLoan.form.sendButton")}
                                            type="submit"
                                            disabled={!fieldsAreValid(formik.values)}
                                            className="btn-primary flex"
                                        />
                                    </GridTable.Data>
                                </GridTable.Container>
                            </GridTable>
                        </Form>
                    )}
                </Formik>
            </MainContainer>
        </Fragment>
    );
};

CustomLoanForm.propTypes = {
    fetching: bool.isRequired,
    destinyFundList: arrayOf(shape({})).isRequired,
    minAmount: number.isRequired,
};

const mapStateToProps = (state) => ({
    fetching: loansSelectors.getFetching(state),
    destinyFundList: loansSelectors.getDestinyFundList(state),
    minAmount: loansSelectors.getMinAmount(state),
    documentationBackup: environmentSelectors.getDocumentationBackup(state),
    documentationRequest: environmentSelectors.getDocumentationRequest(state),
    destinyFund: state.loans.destinyFundList?.find((destiny) => destiny.value == state.loans.destinyFund),
    amount: state.loans.amount,
});

export default connect(mapStateToProps)(CustomLoanForm);
